import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Button,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import DownloadIcon from "@mui/icons-material/Download";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { colors } from "../../../theme/colors";
import { CustomStyledCircularProgress } from "../../../components/common-components/CustomStyledCircularProgress";

const formatText = (text) => {
  if (!text) return "";
  return text
    .replace(/([a-z])([A-Z])/g, "$1 $2") 
    .replace(/([a-zA-Z]):([a-zA-Z0-9])/g, "$1: $2")
    .replace(/,([a-zA-Z])/g, ", $1") 
    .replace(/\s+/g, " ") 
    .trim();
};

const CandidateDetailsModal = ({
  open,
  onClose,
  candidate,
  handleDownloadResume,
  isDownloading,
}) => {
  if (!candidate) return null;

  const metadata = candidate.metadata || {};
  const skills = metadata.skills || [];
  const experience = metadata.experience || [];
  const certifications = metadata.certifications || [];
  const education = metadata.education || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.12)",
          backgroundColor: "#ffffff",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={metadata.profilePic}
              alt={metadata.name}
              sx={{
                width: 56,
                height: 56,
                border: "1px solid #e2e8f0",
              }}
            />
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#1e293b",
                    fontSize: "1.2rem",
                  }}
                >
                  {metadata.name || "Candidate Details"}
                </Typography>
                {candidate.final_score && (
                  <Chip
                    label={`${candidate.final_score}% Match`}
                    size="small"
                    sx={{
                      backgroundColor: "#f1f5f9",
                      color: "#475569",
                      border: "1px solid #cbd5e1",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      borderRadius: "6px",
                    }}
                  />
                )}
              </Box>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.25 }}>
                {metadata.title}
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={onClose}
            aria-label="close"
            sx={{
              color: "#64748b",
              "&:hover": {
                backgroundColor: "#f1f5f9",
                color: "#0f172a",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 3,
          maxHeight: "68vh",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f5f9",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#cbd5e1",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: colors.primary,
            },
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 2,
            mb: 3,
            mt: 0.5,
          }}
        >
          {metadata.location && metadata.location.length > 0 && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                gap: 1.25,
              }}
            >
              <LocationOnIcon fontSize="small" sx={{ color: "#64748b" }} />
              <Box sx={{ overflow: "hidden" }}>
                <Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>
                  Location
                </Typography>
                <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 500 }}>
                  {metadata.location.join(", ")}
                </Typography>
              </Box>
            </Box>
          )}

          {metadata.email && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                gap: 1.25,
              }}
            >
              <EmailIcon fontSize="small" sx={{ color: "#64748b" }} />
              <Box sx={{ overflow: "hidden" }}>
                <Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>
                  Email
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#1e293b",
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {metadata.email}
                </Typography>
              </Box>
            </Box>
          )}

          {metadata.phone_number && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                gap: 1.25,
              }}
            >
              <PhoneIcon fontSize="small" sx={{ color: "#64748b" }} />
              <Box sx={{ overflow: "hidden" }}>
                <Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>
                  Phone
                </Typography>
                <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 500 }}>
                  {metadata.phone_number}
                </Typography>
              </Box>
            </Box>
          )}

          {metadata.years_of_experience !== undefined && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                gap: 1.25,
              }}
            >
              <WorkIcon fontSize="small" sx={{ color: "#64748b" }} />
              <Box sx={{ overflow: "hidden" }}>
                <Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>
                  Experience
                </Typography>
                <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 500 }}>
                  {metadata.years_of_experience} Years
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {education && education.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <SchoolIcon fontSize="small" sx={{ color: "#64748b" }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                Education
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, pl: 3.25 }}>
              {education
                .flatMap((item) => (typeof item === "string" ? item.split("•") : [item]))
                .map((eduItem, idx) => {
                  const formatted = formatText(eduItem);
                  if (!formatted) return null;
                  return (
                    <Box key={idx} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                      <Typography variant="body2" sx={{ color: colors.primary, fontWeight: 700, lineHeight: 1.6 }}>
                        •
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.65, fontWeight: 500 }}>
                        {formatted}
                      </Typography>
                    </Box>
                  );
                })}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2.5 }} />

        {skills && skills.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 1.5 }}>
              Skills ({skills.length})
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {skills.map((skill, idx) => (
                <Chip
                  key={idx}
                  label={formatText(skill)}
                  size="small"
                  sx={{
                    backgroundColor: "#f8fafc",
                    color: "#334155",
                    border: "1px solid #e2e8f0",
                    fontWeight: 500,
                    fontSize: "0.8rem",
                    borderRadius: "6px",
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2.5 }} />

        {experience && experience.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 1.5 }}>
              Recent Work Experience
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, pl: 1 }}>
              {experience.map((exp, idx) => {
                const formatted = formatText(exp);
                return (
                  <Typography key={idx} variant="body2" sx={{ color: "#334155", lineHeight: 1.65, fontWeight: 500 }}>
                    • {formatted}
                  </Typography>
                );
              })}
            </Box>
          </Box>
        )}

        {certifications && certifications.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <WorkspacePremiumIcon fontSize="small" sx={{ color: "#64748b" }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                Certifications
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pl: 3.25 }}>
              {certifications.map((cert, idx) => {
                const formatted = formatText(cert);
                return (
                  <Typography key={idx} variant="body2" sx={{ color: "#475569", lineHeight: 1.6 }}>
                    • {formatted}
                  </Typography>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: "#64748b",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Close
        </Button>

        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleDownloadResume(
              candidate.id,
              metadata.name,
              `modal_${candidate.id}`
            );
          }}
          disabled={isDownloading}
          sx={{
            backgroundColor: colors.primary,
            color: "#ffffff",
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            py: 0.8,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: colors.primaryHover,
              boxShadow: "none",
            },
          }}
        >
          {isDownloading ? (
            <>
              Downloading...
              <CustomStyledCircularProgress size={18} sx={{ ml: 1, color: "#ffffff" }} />
            </>
          ) : (
            "Download Resume"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CandidateDetailsModal;
